
import { Component, ChangeDetectionStrategy, input, output, signal, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../pipes/translate.pipe';

declare var JitsiMeetExternalAPI: any;

@Component({
  selector: 'app-video-call-modal',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './video-call-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoCallModalComponent implements OnDestroy {
  isOpen = input.required<boolean>();
  twilioToken = input<string | null>(); // Kept for compatibility, unused
  roomName = input<string | null>();
  displayName = input<string>(''); // New input for user's name
  
  // Distinguish between incoming call (Patient) and outgoing call (Doctor)
  isIncoming = input<boolean>(false); 

  callEnded = output<void>();
  imageCaptured = output<string>();
  
  // Track if call has been accepted locally (Switch view to Video)
  hasAccepted = signal(false);

  @ViewChild('jitsiContainer') jitsiContainer!: ElementRef;
  private jitsiApi: any;

  joinCall(): void {
    const room = this.roomName();
    if (room) {
        this.hasAccepted.set(true);
        // Wait for view to update so the #jitsiContainer div exists in DOM
        setTimeout(() => this.startConference(), 100);
    }
  }

  acceptIncomingCall(): void {
      this.joinCall();
  }

  declineIncomingCall(): void {
      this.close();
  }

  startConference() {
    try {
        if (!this.jitsiContainer) {
            console.error("Jitsi container element not found.");
            return;
        }

        const domain = 'meet.jit.si';
        const options = {
            roomName: this.roomName(),
            width: '100%',
            height: '100%',
            parentNode: this.jitsiContainer.nativeElement,
            userInfo: {
                displayName: this.displayName() || 'User'
            },
            // AGGRESSIVE CONFIGURATION TO SKIP PREJOIN & LOGIN
            configOverwrite: { 
                disableDeepLinking: true, // Prevents mobile app promotion
                startWithAudioMuted: false,
                startWithVideoMuted: false,
                prejoinPageEnabled: false, // Explicitly disable prejoin
                skipMeetingPrejoin: true,  // Skip prejoin for real
                enableWelcomePage: false,  // No welcome page
                readOnlyName: true,        // User cannot change name
                requireDisplayName: false, // Do not require display name
                // Disable features that might confuse user or cause redirects
                disableThirdPartyRequests: true,
                breakoutRooms: {
                    hideAddRoomButton: true,
                    hideAutoAssignButton: true,
                    hideJoinRoomButton: true
                }
            },
            interfaceConfigOverwrite: {
                SHOW_JITSI_WATERMARK: false,
                SHOW_WATERMARK_FOR_GUESTS: false,
                SHOW_BRAND_WATERMARK: false,
                HIDE_DEEP_LINKING_LOGO: true,
                SHOW_PROMOTIONAL_CLOSE_PAGE: false,
                // RESTRICTED TOOLBAR: Removes 'profile', 'settings', 'security' to prevent Login clicks
                TOOLBAR_BUTTONS: [
                    'microphone', 'camera', 'hangup', 
                    'tileview', 'fullscreen', 
                    'chat', 'raisehand', 
                    'videoquality'
                ]
            }
        };
        
        console.log('Initializing Jitsi with aggressive options:', options);
        this.jitsiApi = new JitsiMeetExternalAPI(domain, options);
        
        // Handle when user clicks Hangup inside Jitsi
        // FIX: Just log this event. Closing immediately causes issues if the user is redirected (e.g. to login).
        this.jitsiApi.addEventListener('videoConferenceLeft', (e: any) => {
            console.log('Video Conference Left (Event Logged):', e);
        });

        // FIX: Use readyToClose as the primary trigger to close the modal.
        // This fires when the call is truly finished from the user's perspective.
        this.jitsiApi.addEventListener('readyToClose', () => {
            console.log('Jitsi Ready to Close');
            this.close();
        });

        // Optional: Monitor errors
        this.jitsiApi.addEventListener('error', (e: any) => {
             console.error('Jitsi Error:', e);
        });

    } catch (e) {
        console.error('Failed to start Jitsi conference', e);
    }
  }

  close(): void {
    if (this.jitsiApi) {
        this.jitsiApi.dispose(); // Critical: Releases camera/mic
        this.jitsiApi = null;
    }
    this.hasAccepted.set(false);
    this.callEnded.emit();
  }

  ngOnDestroy() {
    if (this.jitsiApi) {
        this.jitsiApi.dispose();
    }
  }
}
